// src/hooks/useCalendarEvents.js
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { mapEventsForCalendar } from "../utils/eventMapper";
import { normalizeEvents } from "../utils/eventNormalizer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000";

export function useCalendarEvents(firebaseUser) {
  const [events, setEvents] = useState([]);
  const [work, setWork] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ===== Fetch events + work =====
  const fetchAllEvents = useCallback(async () => {
    if (!firebaseUser) return;

    try {
      const token = await firebaseUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [eventRes, workRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/event/`, { headers, withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/work/`, { headers, withCredentials: true }),
      ]);

      setEvents(normalizeEvents(eventRes.data) || []);
      setWork(normalizeEvents(workRes.data) || []);
    } catch (err) {
      console.error("Error fetching events/work:", err.response?.data || err.message);
      setEvents([]);
      setWork([]);
      setError(err);
    }
  }, [firebaseUser]);

  // ===== Fetch holidays =====
  const fetchHolidays = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/holidays`);
      setHolidays(res.data || []);
    } catch (err) {
      console.error("Error fetching holidays:", err);
      setHolidays([]);
    }
  }, []);

  // ===== Fetch birthdays =====
  const fetchBirthdays = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/birthdays`);
      setBirthdays(res.data || []);
    } catch (err) {
      console.error("Error fetching birthdays:", err);
      setBirthdays([]);
    }
  }, []);

  // ===== Load all data =====
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAllEvents(), fetchHolidays(), fetchBirthdays()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAllEvents, fetchHolidays, fetchBirthdays]);

  // ===== Map holidays and birthdays =====
  const mappedHolidays = holidays.map((h) => ({
    id: `holiday-${h.id}`,
    title: h.holiday_name,
    start: h.calendar_date,
    end: h.calendar_date,
    allDay: true,
    extendedProps: { event_type: "holiday" },
  }));

  const mappedBirthdays = birthdays.map((b) => ({
    id: `birthday-${b.id}`,
    title: `🎉 ${b.employee_name}'s Birthday`,
    start: b.calendar_date,
    end: b.calendar_date,
    allDay: true,
    extendedProps: { event_type: "birthday", employee_name: b.employee_name },
  }));

  // ===== Combine all events =====
  const filteredEvents = [
    ...mapEventsForCalendar(events, "event"),
    ...mapEventsForCalendar(work, "work"),
    ...mappedHolidays,
    ...mappedBirthdays,
  ].filter(Boolean);

  // ===== Helpers =====
  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  };

  const getTitleColor = (title, type, primaryTechnician = "") => {
    const t = title?.toLowerCase() || "";
    const tn = primaryTechnician?.toLowerCase() || "";

    if (type === "birthday") return "text-black border-white-600 bg-orange-300";
    if (type === "holiday") return "text-white border-black bg-black";

    if (type === "work" || type === "event") {
      if (t.includes("travel") || t.includes("drive") || t.includes("van"))
        return "text-black border-yellow-600 bg-yellow-100";
      if (t.includes("office")) return "text-black border-blue-600 bg-blue-100";
      if (
        t.includes("personal leave") ||
        t.includes("time off") ||
        t.includes("pto") ||
        t.includes("rest")
      )
        return "text-black border-green-600 bg-green-100";
      if (tn.includes("fusionsplice") || tn.includes("hightop") || tn.includes("lowtop"))
        return "text-black border-yellow-600 bg-indigo-300";
      if (t.includes("no job")) return "text-black border-gray-600 bg-gray-100";
      return "text-black border-red-600 bg-red-100";
    }

    return "text-black bg-gray-100";
  };

  return { filteredEvents, formatDateTime, getTitleColor, loading, error };
}
